import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  offersApi,
  CHANNEL_META,
  STATUS_META,
  JUDGMENT_META,
} from "../lib/offersApi";
import { useAuth, hasRole } from "../context/AuthContext";
import NewOfferModal from "../components/NewOfferModal";
import { EmptyOffers } from "../components/illustrations/EmptyIllustrations";
import { EmptyState } from "../components/ui/empty-state";
import { GridSkeletonRows } from "../components/ui/grid-skeleton";

const PAGE_SIZE = 50;

function StatusBadge({ status }) {
  const s = STATUS_META[status] || {
    label: status,
    bg: "var(--bg-subtle)",
    fg: "var(--text-secondary)",
  };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`status-${status}`}
    >
      {s.label}
    </span>
  );
}

function JudgmentChip({ judgment }) {
  if (!judgment) return <span className="text-[11px] text-gray-400">—</span>;
  const j = JUDGMENT_META[judgment] || {
    bg: "var(--bg-subtle)",
    fg: "var(--text-secondary)",
  };
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ background: j.bg, color: j.fg }}
      title={judgment}
    >
      {judgment.length > 14 ? judgment.split(" ")[0] : judgment}
    </span>
  );
}

function UserAvatar({ user }) {
  if (!user) return <span className="text-[11px] text-gray-400">—</span>;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[12px]"
      title={`${user.name} (${user.id})`}
    >
      <span
        className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold"
        style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }}
      >
        {user.avatar_initials}
      </span>
      <span style={{ color: "var(--text-primary)" }}>{user.name}</span>
    </span>
  );
}

export default function Offers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(
    hasRole(user, "titolare", "super_admin") ? "to_approve" : "mine"
  );
  const [offers, setOffers] = useState([]);
  const [badges, setBadges] = useState({ mine: 0, to_approve: 0, unassigned: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        scope: tab,
        page,
        page_size: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (channelFilter) params.channel = channelFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await offersApi.list(params);
      setOffers(data.items);
      setTotal(data.total);
      setBadges(data.badges);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento offerte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, channelFilter, statusFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const canApprove = hasRole(user, "titolare", "super_admin");
  const canDelete = hasRole(user, "super_admin");

  const tabs = useMemo(() => {
    const base = [{ key: "mine", label: "Le mie", badge: badges.mine }];
    if (canApprove) {
      base.push(
        {
          key: "to_approve",
          label: "Da approvare",
          badge: badges.to_approve,
          urgent: badges.to_approve > 0,
        },
        {
          key: "unassigned",
          label: "Da elaborare",
          badge: badges.unassigned,
        }
      );
    }
    base.push({ key: "all", label: "Tutte" });
    return base;
  }, [badges, canApprove]);

  const openOffer = (o) => nav(`/workbench?session=${o.id}`);

  const doDelete = async (o) => {
    if (!window.confirm(`Eliminare offerta ${o.offer_number}?`)) return;
    try {
      await offersApi.delete(o.id);
      toast.success("Offerta eliminata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div
      className="max-w-[1600px] mx-auto p-6 space-y-4"
      data-testid="offers-page"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-[22px] font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Offerte
          </h1>
          <p
            className="text-[13px] mt-0.5"
            style={{ color: "var(--text-secondary)" }}
          >
            Tutte le offerte {canApprove ? "H&C" : "a te assegnate"} · {total} totali
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-[13px] font-medium"
          style={{
            background: "var(--accent-primary)",
            color: "var(--text-on-accent)",
          }}
          data-testid="new-offer-btn"
        >
          <Plus className="h-4 w-4" />
          Nuova offerta
        </button>
      </div>

      {/* Tabs */}
      <div
        className="flex items-center gap-1 border-b"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-[13px] font-medium -mb-px"
            style={{
              color: tab === t.key ? "var(--text-primary)" : "var(--text-secondary)",
              borderBottom: tab === t.key
                ? "2px solid var(--accent-primary)"
                : "2px solid transparent",
            }}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
            {t.badge > 0 && (
              <span
                className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-semibold"
                style={{
                  background: t.urgent ? "#FED7AA" : "var(--bg-subtle)",
                  color: t.urgent ? "#C2410C" : "var(--text-secondary)",
                }}
              >
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5"
            style={{ color: "var(--text-muted)" }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per cliente…"
            className="w-full h-8 pl-8 pr-2 rounded-md text-[12px]"
            style={{
              border: "1px solid var(--border-default)",
              background: "var(--bg-surface)",
            }}
            data-testid="offers-search"
          />
        </div>
        <select
          value={channelFilter}
          onChange={(e) => {
            setChannelFilter(e.target.value);
            setPage(1);
          }}
          className="h-8 px-2 rounded-md text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="offers-channel-filter"
        >
          <option value="">Tutti i canali</option>
          {Object.entries(CHANNEL_META).map(([v, m]) => (
            <option key={v} value={v}>
              {m.icon} {m.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-8 px-2 rounded-md text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="offers-status-filter"
        >
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_META).map(([v, m]) => (
            <option key={v} value={v}>
              {m.label}
            </option>
          ))}
        </select>
        {(channelFilter || statusFilter || search) && (
          <button
            type="button"
            onClick={() => {
              setChannelFilter("");
              setStatusFilter("");
              setSearch("");
              setPage(1);
            }}
            className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-[12px]"
            style={{ color: "var(--text-muted)" }}
            data-testid="offers-reset-filters"
          >
            <X className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      {/* Table */}
      <div
        className="rounded-lg overflow-hidden"
        style={{
          border: "1px solid var(--border-subtle)",
          background: "var(--bg-surface)",
        }}
      >
        <table className="w-full text-[12px]" data-testid="offers-table">
          <thead>
            <tr
              style={{
                background: "var(--bg-subtle)",
                color: "var(--text-secondary)",
              }}
            >
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Offerta
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Cliente
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Canale
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Stato
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Assegnatario
              </th>
              <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase">
                Valore
              </th>
              <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase">
                Ricarico
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Giudizio
              </th>
              <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase">
                Aggiornato
              </th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && <GridSkeletonRows cols={10} rows={6} />}
            {!loading && offers.length === 0 && (
              <tr>
                <td colSpan={10}>
                  <EmptyState
                    testid="offers-empty"
                    illustration={<EmptyOffers />}
                    title={
                      search || channelFilter || statusFilter
                        ? "Nessuna offerta corrisponde ai filtri"
                        : tab === "to_approve"
                        ? "Nessuna offerta in attesa di approvazione"
                        : tab === "unassigned"
                        ? "Nessuna offerta da elaborare"
                        : "Nessuna offerta"
                    }
                    description={
                      search || channelFilter || statusFilter
                        ? "Prova a modificare o rimuovere i filtri attivi."
                        : 'Crea la prima offerta con il bottone "Nuova offerta" in alto.'
                    }
                  />
                </td>
              </tr>
            )}
            {!loading &&
              offers.map((o) => (
                <tr
                  key={o.id}
                  className="cursor-pointer hover:bg-slate-50"
                  style={{ borderTop: "1px solid var(--border-subtle)" }}
                  onClick={() => openOffer(o)}
                  data-testid={`offer-row-${o.offer_number}`}
                >
                  <td
                    className="px-3 py-2 tq-cell-mono text-[11px]"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {o.offer_number || o.id.slice(0, 8)}
                  </td>
                  <td
                    className="px-3 py-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {o.cliente?.nome_azienda || "—"}
                    <span
                      className="ml-1.5 text-[10px]"
                      style={{ color: "var(--text-muted)" }}
                    >
                      · {o.voci_count} voci
                    </span>
                  </td>
                  <td className="px-3 py-2" title={CHANNEL_META[o.channel]?.label}>
                    <span className="text-[14px]">
                      {CHANNEL_META[o.channel]?.icon || "✏️"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={o.workflow_status} />
                  </td>
                  <td className="px-3 py-2">
                    <UserAvatar user={o.assigned_to} />
                  </td>
                  <td
                    className="px-3 py-2 text-right tq-cell-mono"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {o.value_total != null
                      ? `€ ${Number(o.value_total).toFixed(2)}`
                      : "—"}
                  </td>
                  <td
                    className="px-3 py-2 text-right tq-cell-mono"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {o.markup_applied != null
                      ? `${Number(o.markup_applied).toFixed(0)}%`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <JudgmentChip judgment={o.judgment} />
                  </td>
                  <td
                    className="px-3 py-2 text-[11px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {o.updated_at
                      ? new Date(o.updated_at).toLocaleString("it-IT", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="px-2 py-2">
                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          doDelete(o);
                        }}
                        className="h-6 w-6 inline-flex items-center justify-center rounded"
                        style={{ color: "#DC2626" }}
                        title="Elimina (super_admin)"
                        data-testid={`delete-${o.offer_number}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between text-[12px]"
          style={{ color: "var(--text-secondary)" }}
        >
          <div>
            Pagina {page} di {Math.ceil(total / PAGE_SIZE)} · {total} totali
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="h-7 px-2 rounded disabled:opacity-40"
              style={{ border: "1px solid var(--border-default)" }}
            >
              ‹ Prec
            </button>
            <button
              type="button"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage(page + 1)}
              className="h-7 px-2 rounded disabled:opacity-40"
              style={{ border: "1px solid var(--border-default)" }}
            >
              Succ ›
            </button>
          </div>
        </div>
      )}

      {showNewModal && (
        <NewOfferModal
          onClose={() => setShowNewModal(false)}
          onSelected={async (channel) => {
            try {
              const created = await offersApi.create(channel);
              toast.success(`✓ Offerta ${created.offer_number} creata`);
              nav(`/?session=${created.session_id || created.id}`);
            } catch (e) {
              toast.error(
                e?.response?.data?.detail || "Errore creazione offerta"
              );
            }
          }}
        />
      )}
    </div>
  );
}
